/**
 * Version Broadcast & Auto-Reload Service
 * 
 * Provides:
 * 1. Real-time broadcast reload synchronization across all active student & lecturer tabs via Firestore (settings/system_version).
 * 2. Periodic fallback version check (/version.json) when new builds are deployed.
 * 3. Neobrutalist high-contrast update banner with automatic countdown reload.
 * 4. Admin trigger function (window.broadcastAppRefresh) to refresh all connected users instantly.
 */

import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore'
import { db, auth } from './firebase/index.js'

export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.5.0'
export const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? Number(__BUILD_TIME__) : Date.now()
export const CLIENT_START_TIME = Date.now()

// Expose runtime metadata
if (typeof window !== 'undefined') {
  window.__PORTAL_VERSION__ = APP_VERSION
  window.__PORTAL_BUILD_TIME__ = BUILD_TIME
  window.__PORTAL_START_TIME__ = CLIENT_START_TIME
}

/**
 * Safely purge client caches and reload to the latest bundle
 */
export function performSafeReload() {
  try {
    // 1. Purge CacheStorage if any Service Worker / PWA caches exist
    if ('caches' in window) {
      caches.keys().then((names) => {
        return Promise.all(names.map((name) => caches.delete(name)))
      }).catch(() => {})
    }

    // 2. Clear transient chunk retry keys
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('chunk_reload_retry_')) {
        sessionStorage.removeItem(key)
      }
    })
  } catch (e) {
    console.warn('[VersionBroadcast] Cache purge warning:', e)
  }

  // 3. Construct cache-busted URL without losing route path
  try {
    const url = new URL(window.location.href)
    url.searchParams.set('_v', Date.now().toString())
    window.location.replace(url.toString())
  } catch (e) {
    window.location.reload()
  }
}

/**
 * Display Neobrutalist Countdown Update Notice
 */
export function showUpdateBanner({
  title = 'Pembaruan Sistem Tersedia',
  message = 'Sistem Portal Sinau telah diperbarui ke versi terbaru. Memperbarui halaman...',
  countdown = 3,
  forceImmediate = false,
  version = APP_VERSION
} = {}) {
  if (typeof document === 'undefined') return
  if (forceImmediate) {
    performSafeReload()
    return
  }

  // Prevent duplicate banners
  if (document.getElementById('portal-system-update-banner')) return

  const banner = document.createElement('div')
  banner.id = 'portal-system-update-banner'
  banner.setAttribute('role', 'alert')
  banner.setAttribute('aria-live', 'assertive')

  banner.style.cssText = `
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 999999;
    width: 90%;
    max-width: 480px;
    background: #ffffff;
    border: 3px solid #111111;
    border-radius: 12px;
    box-shadow: 6px 6px 0px #111111;
    padding: 1.25rem 1.5rem;
    font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #111111;
    animation: neoBannerSlide 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  `

  let remainingSec = Math.max(1, parseInt(countdown, 10) || 3)

  banner.innerHTML = `
    <style>
      @keyframes neoBannerSlide {
        from { transform: translate(-50%, -40px); opacity: 0; }
        to { transform: translate(-50%, 0); opacity: 1; }
      }
      @keyframes spinFast {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .portal-update-spin {
        animation: spinFast 1.5s linear infinite;
        display: inline-block;
      }
    </style>
    <div style="display: flex; align-items: flex-start; gap: 1rem;">
      <div style="width: 42px; height: 42px; border-radius: 8px; background: #ecfdf5; border: 2px solid #111111; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 2px 2px 0 #111111;">
        <span class="portal-update-spin" style="font-size: 1.25rem;">🔄</span>
      </div>
      <div style="flex: 1; min-width: 0;">
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.25rem;">
          <h4 style="font-size: 1.05rem; font-weight: 800; margin: 0; color: #111111;">${title}</h4>
          <span style="font-size: 0.72rem; font-weight: 700; background: #fef08a; border: 1.5px solid #111111; padding: 2px 6px; border-radius: 4px;">v${version}</span>
        </div>
        <p style="font-size: 0.85rem; color: #4b5563; line-height: 1.4; margin: 0 0 0.75rem 0;">${message}</p>
        
        <!-- Progress bar -->
        <div style="height: 6px; width: 100%; background: #e5e7eb; border: 1.5px solid #111111; border-radius: 999px; overflow: hidden; margin-bottom: 0.85rem;">
          <div id="portal-update-prog" style="height: 100%; width: 100%; background: #4f46e5; transition: width 1s linear;"></div>
        </div>

        <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;">
          <span style="font-size: 0.78rem; font-weight: 700; color: #6b7280;">
            Otomatis dalam <span id="portal-update-count" style="color: #4f46e5; font-size: 0.95rem;">${remainingSec}</span>d...
          </span>
          <button id="portal-update-now-btn" style="
            background: #4f46e5;
            color: #ffffff;
            border: 2px solid #111111;
            box-shadow: 2px 2px 0 #111111;
            padding: 0.4rem 0.9rem;
            font-size: 0.82rem;
            font-weight: 800;
            border-radius: 6px;
            cursor: pointer;
            transition: transform 0.1s ease;
          ">
            Perbarui Sekarang 🚀
          </button>
        </div>
      </div>
    </div>
  `

  document.body.appendChild(banner)

  const progEl = document.getElementById('portal-update-prog')
  const countEl = document.getElementById('portal-update-count')
  const nowBtn = document.getElementById('portal-update-now-btn')

  const totalSec = remainingSec

  if (nowBtn) {
    nowBtn.addEventListener('click', () => {
      performSafeReload()
    })
  }

  const intervalId = setInterval(() => {
    remainingSec -= 1
    if (countEl) countEl.textContent = String(remainingSec)
    if (progEl) {
      const pct = Math.max(0, (remainingSec / totalSec) * 100)
      progEl.style.width = pct + '%'
    }

    if (remainingSec <= 0) {
      clearInterval(intervalId)
      performSafeReload()
    }
  }, 1000)
}

/**
 * Initialize Firestore Broadcast Listener
 * Listens to doc 'settings/system_version'
 */
export function initVersionBroadcastListener() {
  if (typeof window === 'undefined') return

  try {
    const versionRef = doc(db, 'settings', 'system_version')

    onSnapshot(versionRef, (snap) => {
      if (!snap.exists()) return
      const data = snap.data() || {}
      const refreshTs = Number(data.refreshTimestamp || 0)
      if (!refreshTs) return

      // Filter: only handle broadcasts emitted after client started,
      // and that haven't been processed in this session yet
      const lastHandled = Number(sessionStorage.getItem('last_handled_broadcast_ts') || 0)
      if (refreshTs > CLIENT_START_TIME && refreshTs > lastHandled) {
        sessionStorage.setItem('last_handled_broadcast_ts', String(refreshTs))
        console.log('[VersionBroadcast] Received broadcast refresh signal:', data)

        // Check if targeted to specific role
        const targetRole = String(data.targetRole || 'all').toLowerCase()
        const currentRole = String(
          window.AppState?.role ||
          JSON.parse(localStorage.getItem('portal_user') || '{}')?.role ||
          'all'
        ).toLowerCase()

        if (targetRole !== 'all' && targetRole !== currentRole) {
          return // Not targeted to this user's role
        }

        showUpdateBanner({
          title: data.title || 'Pembaruan Sistem Tersedia',
          message: data.message || 'Sistem telah diperbarui oleh administrator. Memperbarui halaman...',
          countdown: data.countdown != null ? data.countdown : 3,
          forceImmediate: Boolean(data.forceImmediate),
          version: data.version || APP_VERSION
        })
      }
    }, (err) => {
      // Permission or network error (non-blocking)
      console.warn('[VersionBroadcast] Firestore listener status:', err.message)
    })
  } catch (err) {
    console.warn('[VersionBroadcast] Failed to initialize Firestore listener:', err)
  }
}

/**
 * Check version.json artifact for new build deployment
 */
export async function checkBuildArtifactUpdate() {
  if (typeof window === 'undefined') return
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
    })
    if (!res.ok) return
    const info = await res.json()

    if (info.buildTime && BUILD_TIME && info.buildTime > BUILD_TIME) {
      // Prevent rapid re-triggering if a reload was already initiated in the last 20 seconds
      const lastAttempt = Number(sessionStorage.getItem('last_version_reload_attempt') || 0)
      if (Date.now() - lastAttempt < 20000) return

      sessionStorage.setItem('last_version_reload_attempt', String(Date.now()))
      console.log('[VersionBroadcast] Newer build artifact detected:', info)
      showUpdateBanner({
        title: 'Versi Baru Tersedia',
        message: `Pembaruan sistem Portal Sinau (${info.version || 'v' + APP_VERSION}) telah siap. Memperbarui halaman otomatis...`,
        countdown: 3,
        version: info.version || APP_VERSION
      })
    }
  } catch (err) {
    // Non-blocking network check
  }
}

/**
 * Start periodic version check and tab visibility triggers
 */
export function initPeriodicVersionCheck(intervalMs = 3 * 60 * 1000) {
  if (typeof window === 'undefined') return

  // Check on tab focus / switch back
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkBuildArtifactUpdate()
    }
  })

  // Check on window online event
  window.addEventListener('online', () => {
    checkBuildArtifactUpdate()
  })

  // Regular periodic poll
  setInterval(() => {
    checkBuildArtifactUpdate()
  }, intervalMs)
}

/**
 * Broadcast refresh command to ALL active clients via Firestore
 * Only admins can write to settings/system_version per firestore.rules
 */
export async function broadcastSystemRefresh({
  message = 'Pembaruan sistem Portal Sinau terbaru telah tersedia.',
  countdown = 3,
  forceImmediate = false,
  role = 'all'
} = {}) {
  const ts = Date.now()
  const versionRef = doc(db, 'settings', 'system_version')

  const payload = {
    version: APP_VERSION,
    refreshTimestamp: ts,
    message,
    countdown,
    forceImmediate,
    targetRole: role,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.email || auth.currentUser?.uid || 'admin'
  }

  await setDoc(versionRef, payload, { merge: true })
  console.log('[VersionBroadcast] Broadcast signal transmitted successfully:', payload)

  return { success: true, timestamp: ts, payload }
}

// Global exposure for legacy & console administration
if (typeof window !== 'undefined') {
  window.broadcastAppRefresh = broadcastSystemRefresh
  window.checkAppVersionUpdate = checkBuildArtifactUpdate
  window.performSafeAppReload = performSafeReload
  window.VersionBroadcastService = {
    APP_VERSION,
    BUILD_TIME,
    performSafeReload,
    showUpdateBanner,
    broadcastSystemRefresh,
    checkBuildArtifactUpdate
  }
}
